import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { faRefresh } from "@fortawesome/pro-solid-svg-icons/faRefresh";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { parse } from "date-fns";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { OrganizationSetting } from "@mapmycustomers/shared";
import type TimeZone from "@mapmycustomers/shared/enum/TimeZone";
import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import TimeZones from "@app/enum/TimeZones";
import { getEmailQuota, isEmailQueueLoading } from "@app/store/email";
import { fetchEmailQueue, fetchEmailQuota, hideEmailQueue } from "@app/store/email/actions";
import { getBulkEmailSetting, getOrganizationSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { formatDate } from "@app/util/formatters";

import styles from "./EmailQueueSubtitle.module.scss";
import messages from "./messages";

const parseSettingTime = (time: string) => parse(time, "HH:mm", new Date());

const EmailQueueSubtitle = ({
  bulkEmailSetting,
  emailQueueLoading,
  emailQuota,
  onClose,
  onFetchEmailQueue,
  onFetchEmailQuota,
  timezoneSettingValue,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();

  const handleRefreshClick = useCallback(() => {
    onFetchEmailQueue();
    onFetchEmailQuota();
  }, [onFetchEmailQueue, onFetchEmailQuota]);

  const handleRedirectToSettingsClick = useCallback(() => {
    onClose();
    history.push(`${Path.SETTINGS}/${SettingPath.PERSONAL_EMAIL_PREFERENCES}`);
  }, [history, onClose]);

  if (!emailQuota) {
    return null;
  }

  return (
    <div className={styles.container}>
      <span>
        {intl.formatMessage(messages.emailsQueuedLabel, {
          submitted: emailQuota.submitted + emailQuota.processing,
        })}
      </span>
      <Tooltip title={intl.formatMessage(messages.refreshTooltip)}>
        <Button
          icon={<FontAwesomeIcon icon={faRefresh} />}
          loading={emailQueueLoading}
          onClick={handleRefreshClick}
          size="small"
          type="text"
        />
      </Tooltip>
      {bulkEmailSetting && (
        <>
          <span className={styles.separator} />
          <span>
            {intl.formatMessage(messages.sendIntervalLabel, {
              end: formatDate(parseSettingTime(bulkEmailSetting.value.endTime), "p"),
              start: formatDate(parseSettingTime(bulkEmailSetting.value.startTime), "p"),
              timezone: TimeZones[timezoneSettingValue as TimeZone].defaultMessage as ReactNode,
            })}
          </span>
          <Tooltip title={intl.formatMessage(messages.redirectToSettingsTooltip)}>
            <Button
              icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              loading={emailQueueLoading}
              onClick={handleRedirectToSettingsClick}
              size="small"
              type="text"
            />
          </Tooltip>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  bulkEmailSetting: getBulkEmailSetting(state),
  emailQueueLoading: isEmailQueueLoading(state),
  emailQuota: getEmailQuota(state),
  timezoneSettingValue: getOrganizationSettingValue(state, OrganizationSetting.TIMEZONE),
});

const mapDispatchToProps = {
  onClose: hideEmailQueue,
  onFetchEmailQueue: fetchEmailQueue.request,
  onFetchEmailQuota: fetchEmailQuota.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailQueueSubtitle);
